// import components
import { Box } from '@mui/material';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { DetailsDialog } from './components/DetailsDialog';
import DisconnectConfirmation from './components/DisconnectConfirmation';
import FilterBar from './components/FilterBar';

// import hooks
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetColumns } from './hooks/useGetColumns';
import useGetFlexHistoryValues from './hooks/useGetFlexHistoryValues';

// import state
import { RecoilRoot } from 'recoil';

// import types
import type { FC } from 'react';
import type { RootState } from 'redux/rootReducer';
import type { FilterStateTypes } from './FlexHistory.types';
import type { FlexHistoryData } from './types';

// import utils
import dayjs from 'dayjs';
import { t } from 'i18next';

const FlexHistory: FC = () => {
  'use memo';

  const columns = useGetColumns();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<FlexHistoryData | null>(null);

  const hasPoolsOrgGrant = useSelector(
    (state: RootState) => state?.grants?.hasPoolsOrgGrant
  );
  const hasClustersOrgGrant = useSelector(
    (state: RootState) => state?.grants?.hasClustersOrgGrant
  );

  const [filterStates, setFilterStates] = useState<FilterStateTypes>({
    costCenterId: null,
    startDate: dayjs(new Date()).startOf('month').toISOString(),
    endDate: dayjs(new Date()).endOf('month').toISOString(),
    clusterId: null,
    poolId: null,
    employeeId: null,
    requestReason: null,
    functionId: null,
    agencyId: null
  });
  const startDate = dayjs(filterStates.startDate).isValid()
    ? dayjs(filterStates.startDate).toISOString()
    : dayjs(new Date()).startOf('month').toISOString();

  const endDate = dayjs(filterStates.endDate).isValid()
    ? dayjs(filterStates.endDate).toISOString()
    : dayjs(new Date()).endOf('month').toISOString();

  const {
    data: { data: rows = [], totalItems = 0 } = {},
    isFetching,
    refetch
  } = useGetFlexHistoryValues(
    startDate,
    endDate,
    filterStates.costCenterId ? filterStates.costCenterId : [],
    hasClustersOrgGrant ? filterStates?.clusterId : null,
    hasPoolsOrgGrant ? filterStates?.poolId : null,
    filterStates?.employeeId,
    filterStates?.requestReason,
    filterStates?.functionId,
    filterStates?.agencyId,
    page,
    pageSize
  );

  const isMounted = useRef(false);

  const handlePageChange = ({ page, pageSize }) => {
    setPage(page);
    setPageSize(pageSize);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  useEffect(() => {
    isMounted.current ? refetch() : (isMounted.current = true);
  }, [page, pageSize, refetch]);

  const handleFiltersChange = (filters) => {
    setFilterStates((prevState) => ({
      ...prevState,
      ...filters
    }));
  };

  const [showDisconnectConfirmation, setShowDisconnectConfirmation] =
    useState(false);
  const [rowIdToDisconnect, setRowIdToDisconnect] = useState<
    number | string | null
  >(null);

  const handleDisconnect = (id: string | number) => {
    setRowIdToDisconnect(id);
    setShowDisconnectConfirmation(true);
  };

  return (
    <>
      <Box p={2} pt={3}>
        <FilterBar
          onFiltersChange={handleFiltersChange}
          onSearch={handleSearch}
          hasPoolsOrgGrant={hasPoolsOrgGrant}
          hasClustersOrgGrant={hasClustersOrgGrant}
        />
        <TableCRUDLayout
          rows={rows}
          columns={columns}
          loading={isFetching}
          dynamicHeight={true}
          isContentRangePagination
          pageSize={pageSize}
          editable={false}
          rowCount={totalItems}
          onPaginationModelChange={handlePageChange}
          page={page}
          onViewClick={(id, row) => {
            setSelectedRow(row);
            setShowNotesDialog(true);
          }}
          FormComponent={() => null}
          customActions={(id, row) => [
            {
              tooltipTitle: t('flexHistory.disconnect'),
              icon: 'link-slash',
              onClick: () => handleDisconnect(id as string),
              hidden: row.deleteReason
            }
          ]}
        />
      </Box>
      {showDisconnectConfirmation && (
        <DisconnectConfirmation
          rowIdToDisconnect={rowIdToDisconnect}
          setShowDisconnectConfirmation={setShowDisconnectConfirmation}
        />
      )}
      {showNotesDialog && (
        <DetailsDialog
          open={showNotesDialog}
          onClose={() => setShowNotesDialog(false)}
          row={selectedRow}
        />
      )}
    </>
  );
};

const RecoilRootFlexHistory = () => (
  <RecoilRoot>
    <FlexHistory />
  </RecoilRoot>
);

export default RecoilRootFlexHistory;
